import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { ConfirmDialog, FormTitle } from 'components';
import { renderRowCreatedAt } from 'utils/renderTableCell';
import { useSnackbar } from 'notistack';
import { getMedia } from 'utils/getImage';
import { CVThemesMockData, themesMockData } from 'mocks/themesMockData';
import { rejectUpdateRequest } from 'services/requests/rejectUpdate';
import { acceptUpdateRequest } from 'services/requests/acceptUpdate';

const INITIAL_STATE = {
  user: '',
  createdAt: '',
  host: '',
  theme: 1,
  image: null,
  address: '',
  about: '',
  phone: '',
  cv: '',
  title: '',
  type: '',
  productSubCategories: [],
  productCategories: [],
};

export const UpdateDetailForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({});

  const { id } = useParams();
  const profileRef = useRef();
  const [request, setRequest] = useState(INITIAL_STATE);
  const [productCategories, setProductCategories] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [categoryListCount, setCategoryListCount] = useState(0);
  const [allCategories, setAllCategories] = useState(false);
  const [blob, setBlob] = useState(null);
  const [categoryCollapseState, setCategoryCollapseState] = useState(false);
  const [updateID, setUpdateID] = useState(null);
  const [rejectModalState, setRejectModalState] = useState(false);
  const [avatar, setAvatar] = useState(null);

  const [updateRequestData, setUpdateRequestsData] = useState([]);

  useEffect(() => {
    axios
      .get(`/admin/site/update/list`, {
        params: {
          site: id,
          page: 1,
          limit: 10,
        },
      })
      .then((res) => {
        setState(res.data?.data?.[0]);
      });
  }, [id]);

  useEffect(() => {
    const payload = {
      ...state,
      user: state?.site?.userID,
      createdAt: state?.site?.updatedAt,
      host: state?.site?.host,
      about: state?.siteConfig?.about?.data,
      phone: state?.siteConfig?.support?.phones?.[0],
      address: state?.siteConfig?.address?.plain,
      cv: state?.siteConfig?.cv?.data,
      theme: state?.siteConfig?.theme,
      productCategories: state?.siteConfig?.productCategories || [],
      title: state?.site?.title,
      type: state?.site?.type,
      productSubCategories: state?.siteConfig?.productSubCategories || [],
    };

    state?.updateRequests?.map((req) => {
      if (req?.usage === 'cv') {
        payload.cv = req?.newValue?.data;
      }

      if (req?.usage === 'about') {
        payload.about = req?.newValue?.data;
      }

      if (req?.usage === 'support') {
        payload.address = req?.newValue?.address?.plain || state?.siteConfig?.address?.plain;
        payload.support = req?.newValue?.phones?.phones?.[0] || state?.siteConfig?.support?.phones?.[0];
        payload.title = req?.newValue?.title || state?.site?.title;
      }

      return req;
    });

    setRequest(payload);
    const avatarRequest = state?.updateRequests?.find((el) => el?.usage === 'avatar');
    if (avatarRequest !== undefined) {
      setAvatar(avatarRequest?.newValue?.hash);
    } else {
      setAvatar(state?.siteConfig?.avatar?.hash);
    }

    setAllCategories(productCategories?.length === categoryList?.length);

    if (state?.siteConfig?.productCategories?.length === 0) {
      setProductCategories(categoryList);
      setAllCategories(true);
    } else {
      setProductCategories(state?.siteConfig?.productCategories);
    }
  }, [state, categoryList, productCategories?.length]);

  const getCategoryList = useCallback(async () => {
    try {
      const response = await axios.get('/admin/category/list');
      const { categories } = response.data;
      setCategoryListCount(response.data.categories.length);
      setCategoryList(categories);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    getCategoryList();
  }, [getCategoryList]);

  const [hostCheck, setHostCheck] = useState({
    ok: false,
    loading: false,
    checked: request.host !== state.host,
  });

  const onRejectUpdateRequest = async () => {
    try {
      const copy = JSON.parse(JSON.stringify(updateRequestData));
      const index = updateRequestData?.findIndex((el) => el?.update === updateID);
      copy[index] = {
        ...copy[index],
        isApproved: false,
      };

      setUpdateRequestsData(copy);
      await rejectUpdateRequest({
        updateID,
        reason: copy[index]?.reason,
        description: copy[index]?.description,
        notifyUser: copy[index]?.notifyUser,
      });
      // await deactivatedRequest(rejected);
      enqueueSnackbar('با موفقیت انجام شد', { variant: 'success' });
      setRejectModalState(false);
      // navigate(-1);
    } catch (error) {
      console.log(error);
    }
  };

  const onRejectUpdate = (update) => {
    setUpdateID(update?._id);
    const index = updateRequestData?.findIndex((el) => el?.update === update?._id);
    if (index === -1) {
      setUpdateRequestsData([
        ...updateRequestData,
        {
          update: update?._id,
          reason: '',
          description: '',
          notifyUser: false,
          isApproved: null,
        },
      ]);
    }
    setRejectModalState(true);
  };

  const onApproveUpdate = async (update) => {
    try {
      await acceptUpdateRequest({
        updateID: update?._id,
      });
      enqueueSnackbar('با موفقیت انجام شد', { variant: 'success' });
      const index = updateRequestData?.findIndex((el) => el?.update === update?._id);
      if (index === -1) {
        setUpdateRequestsData([
          ...updateRequestData,
          {
            update: update?._id,
            reason: '',
            description: '',
            notifyUser: false,
            isApproved: true,
          },
        ]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onAvatarChange = (e) => {
    const imageBlob = URL.createObjectURL(e.target.files[0]);
    setBlob(imageBlob);
    setRequest({ ...request, image: e.target.files[0] });
  };

  const onUploadAvatar = async () => {
    if (!request.image) return;
    try {
      const body = new FormData();
      body.append('site', state?._id);
      body.append('file', request.image);
      const response = await axios.post('/admin/site/editAvatar', body);
      console.log(response);
    } catch (error) {
      console.log(error);
    }
  };

  const categoryWithSub = useMemo(() => {
    const arr = [];

    productCategories?.map((cat) => {
      const childs = [];
      // PARENT WITH ALL CHILDS SELECTED
      if (cat?.children === undefined && !cat?.children?.length) {
        const category = categoryList.find((el) => el?._id === cat?.id);
        if (category) {
          category?.children?.forEach((z, i) => {
            childs.push(z);
          });
        }
        arr.push({ ...cat, children: childs });
      } else {
        const childsArr = [];
        cat?.children?.map((z) => {
          // ALL (PARENT + CHILDS)
          if (request?.productSubCategories?.length === 0 && productCategories?.length === categoryList?.length) {
            childsArr.push(z);
          } else if (request.productSubCategories?.some((c) => c.id === z._id)) {
            // ONLY SELECTED SUBS
            childsArr.push(z);
          }
          return z;
        });
        if (childsArr?.length) {
          arr.push({ ...cat, children: childsArr });
        }
      }

      return '';
    });

    categoryList?.forEach((cat) => {
      const childs = [];
      request?.productSubCategories?.forEach((child) => {
        if (cat?.children?.some((c) => c._id === child?.id)) {
          childs?.push(child);
        }
      });
      const isDuplicate = arr.find((el) => el?._id === cat?._id);
      if (childs?.length && isDuplicate === undefined) {
        arr.push({ ...cat, children: childs });
      }
    });

    return arr;
  }, [productCategories, request, categoryList]);

  const cvUpdate = state?.updateRequests?.find((el) => el?.usage === 'cv');
  const avatarUpdate = state?.updateRequests?.find((el) => el?.usage === 'avatar');
  const aboutUpdate = state?.updateRequests?.find((el) => el?.usage === 'about');
  const supportUpdate = state?.updateRequests?.find((el) => el?.usage === 'support');

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextField
            label="نام کاربری"
            fullWidth
            disabled
            value={request.user}
            onChange={(e) => setRequest({ ...request, user: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="آدرس دامنه"
            fullWidth
            value={request.host}
            onChange={(e) => {
              setHostCheck({ ...hostCheck, checked: false });
              setRequest({ ...request, host: e.target.value });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField label="تاریخ درخواست" fullWidth value={renderRowCreatedAt('fa-IR', state?.createdAt)} />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>تم انتخابی</InputLabel>
            <Select
              label="تم انتخابی"
              value={request.theme}
              onChange={(e) => setRequest({ ...request, theme: e.target.value })}
            >
              {(request?.type === 1 ? themesMockData : CVThemesMockData).map((theme, index) => (
                <MenuItem key={index} value={theme.value}>
                  <ListItemText primary={theme.title} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <FormTitle
          icon="profile_icon"
          label="تصویر شخصی"
          needApprove={avatarUpdate !== undefined}
          onApprove={() => onApproveUpdate(avatarUpdate)}
          isApproved={updateRequestData?.find((el) => el?.update === avatarUpdate?._id)?.isApproved}
          onReject={() => onRejectUpdate(avatarUpdate)}
        />
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-end',
              gap: '1rem',
              margin: '1rem 0',
            }}
          >
            <img
              src={blob || getMedia(avatar)}
              alt="avatar"
              aria-hidden
              style={{
                width: '160px',
                height: '160px',
                borderRadius: '20px',
                boxShadow: '0px 12px 22px rgba(0, 0, 0, 0.09)',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                profileRef.current.click();
                e.target.value = null;
              }}
            />
            <input hidden type="file" ref={profileRef} accept="image/*" onChange={onAvatarChange} />
            <Button variant="contained" sx={{ height: '48px' }} onClick={onUploadAvatar}>
              اصلاح تصویر
            </Button>
          </Box>
        </Grid>
        <FormTitle
          icon="text"
          label="درباره ما"
          needApprove={aboutUpdate !== undefined}
          onApprove={() => onApproveUpdate(aboutUpdate)}
          onReject={() => onRejectUpdate(aboutUpdate)}
          isApproved={updateRequestData?.find((el) => el?.update === aboutUpdate?._id)?.isApproved}
        />

        <Grid item xs={12}>
          <TextField
            multiline
            minRows={6}
            label=""
            fullWidth
            placeholder="چند خط درباره کاربر"
            value={request.about}
            // onChange={(e) => setRequest({ ...request, about: e.target.value })}
          />
        </Grid>
        <FormTitle
          icon="document"
          label="سوابق کاری"
          needApprove={cvUpdate !== undefined}
          onApprove={() => onApproveUpdate(cvUpdate)}
          isApproved={updateRequestData?.find((el) => el?.update === cvUpdate?._id)?.isApproved}
          onReject={() => onRejectUpdate(cvUpdate)}
        />
        <Grid item xs={12}>
          <TextField
            multiline
            minRows={6}
            label=""
            fullWidth
            placeholder="سوابق کاری"
            value={request.cv}
            // onChange={(e) => setRequest({ ...request, cv: e.target.value })}
          />
        </Grid>
        <FormTitle
          icon="headphone"
          label="تنظیمات سایت"
          needApprove={supportUpdate !== undefined}
          onApprove={() => onApproveUpdate(supportUpdate)}
          isApproved={updateRequestData?.find((el) => el?.update === supportUpdate?._id)?.isApproved}
          onReject={() => onRejectUpdate(supportUpdate)}
        />

        <Grid item xs={12}>
          <TextField
            label="عنوان"
            fullWidth
            placeholder="عنوان"
            value={request?.title}
            sx={{ marginBottom: '20px' }}
            // onChange={(e) => setRequest({ ...request, title: e.target.value })}
          />
          <TextField
            label="شماره تماس پشتیبانی"
            fullWidth
            placeholder="شماره تماس پشتیبانی"
            value={request.phone}
            // onChange={(e) => setRequest({ ...request, phone: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="آدرس"
            fullWidth
            placeholder="آدرس"
            value={request.address}
            // onChange={(e) => setRequest({ ...request, address: e.target.value })}
          />
        </Grid>

        {/* <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={categoryListCount === productCategories.length || allCategories}
                onClick={() => {
                  setAllCategories(!allCategories);
                  if (allCategories) {
                    setProductCategories([]);
                  } else {
                    setProductCategories(categoryList.map(({ _id }) => _id));
                  }
                }}
              />
            }
            label="همه ی دسته بندی ها"
          />
        </Grid> */}

        <FormTitle
          icon="box_icon"
          label="دسته بندی ها"
          needApprove={false}
          has_collapse
          collapseState={categoryCollapseState}
          onCollapseChange={() => setCategoryCollapseState(!categoryCollapseState)}
        />
        {categoryCollapseState && (
          <>
            <Grid item xs={12} style={{ paddingTop: '0px' }}>
              {categoryWithSub?.map((cat, index) => (
                <div style={{ display: 'block', margin: '25px 0px' }} key={index}>
                  <Typography sx={{ fontSize: '14px', color: '#222', fontWeight: 700 }}>{cat?.name}</Typography>
                  <Stack
                    flexDirection="row"
                    mt={2}
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={1}
                  >
                    {cat?.children?.map((z, i) => (
                      <Chip
                        key={i}
                        label={z?.name}
                        sx={{ direction: 'rtl' }}
                        onDelete={() => {
                          // handleCheckCategory(product._id)
                        }}
                        deleteIcon={<></>}
                      />
                    ))}
                  </Stack>
                </div>
              ))}
            </Grid>
          </>
        )}
        {/* <Grid item xs={12}>
          <Stack flexDirection="row" justifyContent="flex-end" gap={1}>
            <Button variant="contained" size="large" sx={{ width: 300 }} onClick={() => navigate(-1)}>
              اتمام درخواست
            </Button>
          </Stack>
        </Grid> */}
      </Grid>

      <ConfirmDialog
        open={rejectModalState}
        onClose={() => setRejectModalState(false)}
        rejected={JSON.parse(JSON.stringify(updateRequestData?.find((el) => el?.update === updateID) || {}))}
        setRejected={(data) => {
          const copy = JSON.parse(JSON.stringify(updateRequestData));
          const index = copy?.findIndex((el) => el?.update === updateID);
          copy[index] = data;
          setUpdateRequestsData(copy);
        }}
        action={onRejectUpdateRequest}
      />
    </>
  );
};
